import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QrScannerSignalRService } from '../services/qr-scanner-signalr.service';
import { ScannerPairingService } from '../services/scanner-pairing.service';
import { ScannerDeviceState } from '../models/qr-scanner.models';
import { Html5Qrcode } from 'html5-qrcode';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './qr-scanner.component.html',
  styleUrl: './qr-scanner.component.css'
})
export class QrScannerComponent implements OnInit, OnDestroy {
  @ViewChild('sessionQrReader') sessionQrReader!: ElementRef;
  @ViewChild('productQrReader') productQrReader!: ElementRef;

  // Estados da aplicação
  currentStep: 'scan-session' | 'pairing' | 'confirming' | 'connected' = 'scan-session';
  deviceState: ScannerDeviceState | null = null;
  pairingError: string | null = null;

  // Dígitos que só existem neste aparelho e que o operador digita no desktop.
  // É o que impede que alguém que fotografou a tela consiga parear.
  confirmationCode: string | null = null;
  stationCode: string | null = null;
  confirmSecondsLeft: number = 0;

  // O computador da estação caiu (F5, rede, deploy). O pareamento continua
  // valendo — só não há para onde mandar código enquanto ele não volta.
  stationOnline: boolean = true;
  
  // Scanner de sessão
  sessionScanner: Html5Qrcode | null = null;
  isScanningSession: boolean = false;
  cameraPermissionDenied: boolean = false;
  
  // Scanner de produtos
  productScanner: Html5Qrcode | null = null;
  isScanningProduct: boolean = false;
  isProcessingQRCode: boolean = false;
  lastScannedCodes: Map<string, number> = new Map();
  readonly SCAN_COOLDOWN_MS = 5000;
  currentScannedCode: string | null = null;

  // Status de envio
  lastSentQrCode: string | null = null;
  lastSentTime: string | null = null;
  sendHistory: Array<{qrCode: string, sentAt: string, success: boolean}> = [];

  // Subscriptions
  subscriptions: Subscription[] = [];

  constructor(
    private qrScannerService: QrScannerSignalRService,
    private pairingService: ScannerPairingService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platform: Object
  ) { }

  ngOnInit(): void {
    // Esta tela não exige login: autentica com o token de capacidade obtido
    // no pareamento, que só vale no hub do scanner.
    this.qrScannerService.useDeviceToken();
    this.setupSignalRListeners();

    if (isPlatformBrowser(this.platform)) {
      this.bootstrap();
    }
  }

  ngOnDestroy(): void {
    this.stopSessionScanner();
    this.stopProductScanner();

    // Sai da sessão mas mantém o pareamento: voltar para a tela reconecta
    // sozinho, sem precisar ler o QR da estação de novo.
    this.qrScannerService.leaveScannerSession().catch(() => { });

    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Três caminhos de entrada, nesta ordem:
   * 1. chegou pelo QR da estação, com o código no endereço → pareia;
   * 2. já pareou antes neste aparelho → reconecta com o token guardado;
   * 3. nenhum dos dois → oferece ler o QR pela câmera.
   */
  private async bootstrap(): Promise<void> {
    const code = this.pairingService.consumeCodeFromUrl();

    if (code) {
      if (!await this.pair(code)) return;
    } else if (!this.pairingService.getToken()) {
      return;
    }

    await this.connectDevice();
  }

  /**
   * Ler o QR não pareia: registra uma tentativa e recebe dígitos que só
   * aparecem aqui. O pareamento só acontece quando o operador digita esses
   * dígitos no desktop.
   */
  private async pair(code: string): Promise<boolean> {
    this.currentStep = 'confirming';
    this.pairingError = null;
    this.confirmationCode = null;
    this.cdr.detectChanges();

    try {
      const challenge = await this.pairingService.requestPairing(code);

      this.confirmationCode = challenge.confirmationCode;
      this.stationCode = challenge.stationCode;
      this.confirmSecondsLeft = this.secondsUntil(challenge.expiresAt);
      this.cdr.detectChanges();

      await this.pairingService.waitForConfirmation(
        challenge.attemptId,
        challenge.expiresAt,
        segundos => {
          this.confirmSecondsLeft = segundos;
          this.cdr.detectChanges();
        });

      return true;
    } catch (error: any) {
      this.failPairing(error?.message);
      return false;
    }
  }

  private secondsUntil(iso: string): number {
    return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
  }

  private async connectDevice(): Promise<void> {
    this.currentStep = 'pairing';
    this.cdr.detectChanges();

    try {
      this.spinner.show();
      this.deviceState = await this.qrScannerService.attachScannerDevice();
      this.currentStep = 'connected';
      this.pairingError = null;
      this.stationOnline = this.deviceState.isConnected;

      this.toastr.success(
        this.transloco.translate('qrScanner.toast.connected', { code: this.deviceState.stationCode }),
        this.transloco.translate('qrScanner.toast.titleScanner'),
        { timeOut: 2500 });

      this.cdr.detectChanges();
      setTimeout(() => this.startProductScanner(), 500);
    } catch {
      // Token vencido, dispositivo revogado no desktop ou sessão encerrada:
      // em todos os casos o caminho de volta é ler o QR da estação de novo.
      this.failPairing(this.transloco.translate('qrScanner.toast.sessionGone'));
    } finally {
      this.spinner.hide();
    }
  }

  private failPairing(message?: string): void {
    this.pairingService.clear();
    this.deviceState = null;
    this.confirmationCode = null;
    this.confirmSecondsLeft = 0;
    this.currentStep = 'scan-session';
    const reason = message || this.transloco.translate<string>('qrScanner.toast.pairError');
    this.pairingError = reason;

    this.toastr.error(reason, this.transloco.translate('qrScanner.toast.titleError'), {
      timeOut: 6000
    });
    this.cdr.detectChanges();
  }

  private setupSignalRListeners(): void {
    // Listener para confirmação de envio
    const qrCodeDataReceivedSub = this.qrScannerService.onQrCodeDataReceived$.subscribe((data: {success: boolean, scannedAt: string}) => {
      if (this.lastSentQrCode) {
        this.sendHistory.unshift({
          qrCode: this.lastSentQrCode,
          sentAt: data.scannedAt,
          success: data.success
        });
        this.lastSentTime = data.scannedAt;
        
        if (data.success) {
          this.toastr.success(this.transloco.translate('qrScanner.toast.qrSent'), this.transloco.translate('qrScanner.toast.titleScanner'), {
            timeOut: 2000
          });
        } else {
          this.toastr.error(this.transloco.translate('qrScanner.toast.qrSendError'), this.transloco.translate('qrScanner.toast.titleError'), {
            timeOut: 3000
          });
        }
        
        // Liberar para próxima leitura (mesma estratégia do label-auto-print)
        // A câmera permanece ativa para próxima leitura
        this.lastSentQrCode = null;
        this.isProcessingQRCode = false;
        this.currentScannedCode = null;
        this.cdr.detectChanges();
      }
    });

    // Listener para desconexão da sessão
    const sessionDisconnectedSub = this.qrScannerService.onSessionDisconnected$.subscribe(() => {
      this.toastr.warning(this.transloco.translate('qrScanner.toast.sessionDisconnectedByServer'), this.transloco.translate('qrScanner.toast.titleScanner'), {
        timeOut: 3000
      });
      this.disconnectFromSession();
    });

    // Estação fora do ar não desfaz o pareamento: é diferente de
    // SessionDisconnected, que desfaz.
    const stationOnlineSub = this.qrScannerService.onStationOnline$.subscribe(online => {
      this.stationOnline = online;

      if (online) {
        this.toastr.success(this.transloco.translate('qrScanner.toast.stationBack'), this.transloco.translate('qrScanner.toast.titleScanner'), {
          timeOut: 2500
        });
      } else {
        this.toastr.warning(this.transloco.translate('qrScanner.toast.stationOffline'), this.transloco.translate('qrScanner.toast.titleScanner'), {
          timeOut: 4000
        });
      }

      this.cdr.detectChanges();
    });

    this.subscriptions.push(qrCodeDataReceivedSub, sessionDisconnectedSub, stationOnlineSub);
  }

  // Scanner de Sessão
  async startSessionScanner(): Promise<void> {
    if (this.isScanningSession) {
      console.log('⚠️ Scanner de sessão já está ativo');
      return;
    }

    console.log('🚀 Iniciando scanner de sessão...');

    try {
      this.isScanningSession = true;
      this.cameraPermissionDenied = false;
      
      if (!isPlatformBrowser(this.platform)) {
        console.error('❌ Não está em ambiente browser');
        this.isScanningSession = false;
        return;
      }

      // Forçar detecção de mudanças para renderizar o elemento
      this.cdr.detectChanges();
      
      // Aguardar renderização do elemento
      await new Promise(resolve => setTimeout(resolve, 300));

      const qrCodeId = 'session-qr-reader';
      let element = document.getElementById(qrCodeId);
      
      // Tentar múltiplas vezes se o elemento não estiver disponível
      let attempts = 0;
      while (!element && attempts < 5) {
        console.log(`⏳ Tentativa ${attempts + 1}: Aguardando elemento ${qrCodeId}...`);
        await new Promise(resolve => setTimeout(resolve, 200));
        this.cdr.detectChanges();
        element = document.getElementById(qrCodeId);
        attempts++;
      }
      
      if (!element) {
        console.error('❌ Elemento session-qr-reader não encontrado no DOM após', attempts, 'tentativas');
        this.isScanningSession = false;
        this.toastr.error(this.transloco.translate('qrScanner.toast.initScannerError'), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
        return;
      }

      console.log('✅ Elemento encontrado, inicializando Html5Qrcode...');
      this.sessionScanner = new Html5Qrcode(qrCodeId);

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const minEdgePercentage = 0.7;
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return {
            width: qrboxSize,
            height: qrboxSize
          };
        },
        aspectRatio: 1.0
      };

      console.log('📷 Iniciando câmera...');
      
      // Mesma estratégia do label-auto-print que funciona
      await this.sessionScanner.start(
        { facingMode: 'environment' }, // Usar câmera traseira
        config,
        (decodedText: string) => {
          console.log('✅ QR Code detectado:', decodedText);
          this.onSessionQrCodeScanned(decodedText);
        },
        (errorMessage: string) => {
          // Ignorar erros de leitura (são normais enquanto não detecta QR code)
        }
      );
      
      console.log('✅ Scanner de sessão iniciado com sucesso');
    } catch (error: any) {
      console.error('❌ Erro ao iniciar scanner de sessão:', error);
      console.error('❌ Tipo do erro:', typeof error);
      console.error('❌ Detalhes do erro:', {
        name: error?.name || 'N/A',
        message: error?.message || 'N/A',
        toString: error?.toString() || 'N/A',
        stack: error?.stack || 'N/A'
      });
      
      this.isScanningSession = false;
      
      // Tratar diferentes tipos de erro
      const errorMessage = error?.message || error?.toString() || 'Erro desconhecido ao iniciar scanner';
      const errorName = error?.name || '';
      
      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError' || 
          errorMessage.includes('permission') || errorMessage.includes('Permission')) {
        this.cameraPermissionDenied = true;
        this.toastr.error(this.transloco.translate('qrScanner.toast.cameraDenied'), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
      } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('camera') || errorMessage.includes('Camera')) {
        this.toastr.error(this.transloco.translate('qrScanner.toast.cameraNotFound'), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
      } else {
        this.toastr.error(this.transloco.translate('qrScanner.toast.scannerStartError', { error: errorMessage }), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
      }
    }
  }

  stopSessionScanner(): void {
    if (this.sessionScanner) {
      this.sessionScanner.stop().then(() => {
        this.sessionScanner?.clear();
        this.sessionScanner = null;
        this.isScanningSession = false;
      }).catch((error) => {
        console.error('Erro ao parar scanner de sessão:', error);
        this.isScanningSession = false;
      });
    }
  }

  async onSessionQrCodeScanned(decodedText: string): Promise<void> {
    this.stopSessionScanner();

    const code = this.pairingService.extractPairingCode(decodedText);
    if (!code) {
      this.failPairing(this.transloco.translate('qrScanner.toast.invalidStationQr'));
      return;
    }

    if (await this.pair(code)) {
      await this.connectDevice();
    }
  }

  // Scanner de Produtos
  async startProductScanner(): Promise<void> {
    if (this.isScanningProduct) {
      return;
    }

    try {
      this.isScanningProduct = true;
      this.cameraPermissionDenied = false;
      
      if (!isPlatformBrowser(this.platform)) {
        this.isScanningProduct = false;
        return;
      }

      // Forçar detecção de mudanças para renderizar o elemento
      this.cdr.detectChanges();
      
      // Aguardar renderização do elemento
      await new Promise(resolve => setTimeout(resolve, 300));

      const qrCodeId = 'product-qr-reader';
      let element = document.getElementById(qrCodeId);
      
      // Tentar múltiplas vezes se o elemento não estiver disponível
      let attempts = 0;
      while (!element && attempts < 5) {
        await new Promise(resolve => setTimeout(resolve, 200));
        this.cdr.detectChanges();
        element = document.getElementById(qrCodeId);
        attempts++;
      }
      
      if (!element) {
        throw new Error('Elemento product-qr-reader não encontrado no DOM');
      }

      this.productScanner = new Html5Qrcode(qrCodeId);

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const minEdgePercentage = 0.7;
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return {
            width: qrboxSize,
            height: qrboxSize
          };
        },
        aspectRatio: 1.0
      };

      // Mesma estratégia do label-auto-print que funciona
      await this.productScanner.start(
        { facingMode: 'environment' }, // Usar câmera traseira
        config,
        (decodedText: string) => {
          this.onProductQrCodeScanned(decodedText);
        },
        (errorMessage: string) => {
          // Ignorar erros de leitura (são normais enquanto não detecta QR code)
        }
      );
    } catch (error: any) {
      console.error('Erro ao iniciar scanner de produto:', error);
      this.isScanningProduct = false;
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.cameraPermissionDenied = true;
        this.toastr.error(this.transloco.translate('qrScanner.toast.cameraDeniedShort'), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
      } else {
        this.toastr.error(this.transloco.translate('qrScanner.toast.scannerStartError', { error: error?.message || error }), this.transloco.translate('qrScanner.toast.titleError'), {
          timeOut: 5000
        });
      }
    }
  }

  stopProductScanner(): void {
    if (this.productScanner) {
      this.productScanner.stop().then(() => {
        this.productScanner?.clear();
        this.productScanner = null;
        this.isScanningProduct = false;
      }).catch((error) => {
        console.error('Erro ao parar scanner de produto:', error);
        this.isScanningProduct = false;
      });
    }
  }

  async onProductQrCodeScanned(decodedText: string): Promise<void> {
    // Evitar múltiplas leituras simultâneas (mesma estratégia do label-auto-print)
    if (this.isProcessingQRCode) {
      return;
    }

    const qrCodeData = decodedText.trim();
    const now = Date.now();
    
    // Verificar se este QR Code já foi lido recentemente (mesma estratégia do label-auto-print)
    const lastScanTime = this.lastScannedCodes.get(qrCodeData);
    if (lastScanTime && (now - lastScanTime) < this.SCAN_COOLDOWN_MS) {
      // QR Code já foi lido recentemente, ignorar
      return;
    }

    // Se é o mesmo QR Code que está sendo processado, ignorar completamente
    if (this.currentScannedCode === qrCodeData) {
      return;
    }

    // Marcar este QR Code como sendo processado
    this.currentScannedCode = qrCodeData;
    
    // Registrar este QR Code como lido agora
    this.lastScannedCodes.set(qrCodeData, now);
    
    // Limpar QR Codes antigos do cache (mais de 30 segundos)
    const thirtySecondsAgo = now - 30000;
    for (const [code, timestamp] of this.lastScannedCodes.entries()) {
      if (timestamp < thirtySecondsAgo) {
        this.lastScannedCodes.delete(code);
      }
    }

    // Emitir beep (mesma estratégia do label-auto-print)
    this.playBeep();

    this.isProcessingQRCode = true;
    this.lastSentQrCode = qrCodeData;

    try {
      await this.qrScannerService.sendQrCodeData(qrCodeData);
      this.toastr.info(this.transloco.translate('qrScanner.toast.qrSentData', { data: qrCodeData }), this.transloco.translate('qrScanner.toast.titleScanner'), {
        timeOut: 2000
      });
    } catch (error: any) {
      console.error('Erro ao enviar QR Code:', error);
      this.toastr.error(this.transloco.translate('qrScanner.toast.qrSendError'), this.transloco.translate('qrScanner.toast.titleError'), {
        timeOut: 3000
      });
      this.isProcessingQRCode = false;
      this.lastSentQrCode = null;
      this.currentScannedCode = null;
    }
  }

  /**
   * Desconecta e <b>desfaz o pareamento</b>: o token é descartado e voltar a
   * usar este celular exige ler o QR da estação de novo.
   */
  async disconnectFromSession(): Promise<void> {
    try {
      if (this.deviceState) {
        await this.qrScannerService.leaveScannerSession();
      }
    } catch (error) {
      console.error('Erro ao desconectar da sessão:', error);
    } finally {
      await this.qrScannerService.stopConnection();
      this.pairingService.clear();
      this.stopProductScanner();
      this.deviceState = null;
      this.currentStep = 'scan-session';
      this.pairingError = null;
      this.sendHistory = [];
      this.lastSentQrCode = null;
      this.lastSentTime = null;
      this.cdr.detectChanges();
    }
  }

  playBeep(): void {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('Não foi possível reproduzir o beep:', error);
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateString;
    }
  }
}

